/**
 * SIP call store
 *
 * Holds the current incoming Invitation (ringing) and active Session (in call).
 * Answer/Decline/HangUp are wired to SIP.js accept/reject/bye so the
 * call overlay buttons can control WebRTC calls.
 * Uses selected audio input from webrtcAudioDeviceStore when answering.
 */

import { create } from 'zustand';
import type { Invitation } from 'sip.js';
import type { Session } from 'sip.js';
import { SessionState, Web } from 'sip.js';

import { useWebrtcAudioDeviceStore } from './webrtcAudioDeviceStore';

// ============================================================================
// Types
// ============================================================================

interface SipCallState {
  /** Incoming INVITE not yet accepted/rejected */
  pendingInvitation: Invitation | null;
  /** Active call session (after accept) */
  activeSession: Session | null;
  /** Local mute state */
  isMuted: boolean;
}

interface SipCallActions {
  setPendingInvitation: (invitation: Invitation | null) => void;
  setActiveSession: (session: Session | null) => void;
  /** Accept incoming call (SIP accept). Call from overlay Answer. */
  answer: () => Promise<void>;
  /** Reject incoming call (SIP reject). Call from overlay Decline. */
  decline: () => Promise<void>;
  /** End active call (SIP bye). Call from overlay End. */
  hangUp: () => Promise<void>;
  /** Toggle local microphone mute */
  toggleMute: () => void;
  /** Switch microphone during active call to the currently selected input device. */
  replaceInputTrack: () => Promise<void>;
}

type SipCallStore = SipCallState & SipCallActions;

// ============================================================================
// Store
// ============================================================================

export const useSipCallStore = create<SipCallStore>()((set, get) => ({
  pendingInvitation: null,
  activeSession: null,
  isMuted: false,

  setPendingInvitation: invitation => set({ pendingInvitation: invitation }),

  setActiveSession: session => set({ activeSession: session }),

  answer: async () => {
    const { pendingInvitation, setPendingInvitation, setActiveSession } = get();
    if (!pendingInvitation) return;
    const inputDeviceId = useWebrtcAudioDeviceStore.getState().audioInputDeviceId;
    const constraints: MediaStreamConstraints = {
      audio: inputDeviceId ? { deviceId: { exact: inputDeviceId } } : true,
      video: false,
    };
    try {
      await pendingInvitation.accept({
        sessionDescriptionHandlerOptions: {
          constraints,
        },
      });
      setPendingInvitation(null);
      setActiveSession(pendingInvitation);
      // Attach remote audio when established
      const attachRemote = () => {
        const sdh = pendingInvitation.sessionDescriptionHandler as Web.SessionDescriptionHandler | undefined;
        const remoteStream = sdh?.remoteMediaStream;
        const audio = document.getElementById('sip-remote-audio') as HTMLAudioElement | null;
        if (audio && remoteStream) {
          audio.srcObject = remoteStream;
          audio.play().catch(() => {});
        }
      };

      // Clear active session when call ends (e.g. other party hangs up)
      pendingInvitation.stateChange.addListener(state => {
        if (state === SessionState.Established) {
          attachRemote();
        }
        if (state === SessionState.Terminated) {
          const audio = document.getElementById('sip-remote-audio') as HTMLAudioElement | null;
          if (audio) audio.srcObject = null;
          set({ activeSession: null, isMuted: false });
        }
      });

      // Attempt immediate attach in case SDH is ready
      attachRemote();
    } catch (err) {
      console.warn('[WebRTC] Answer failed', err);
      setPendingInvitation(null);
    }
  },

  decline: async () => {
    const { pendingInvitation, setPendingInvitation } = get();
    if (!pendingInvitation) return;
    try {
      await pendingInvitation.reject();
    } catch (err) {
      console.warn('[WebRTC] Decline failed', err);
    } finally {
      setPendingInvitation(null);
    }
  },

  hangUp: async () => {
    const { activeSession, setActiveSession } = get();
    if (!activeSession) return;
    try {
      await activeSession.bye();
    } catch (err) {
      console.warn('[WebRTC] HangUp failed', err);
    } finally {
      const audio = document.getElementById('sip-remote-audio') as HTMLAudioElement | null;
      if (audio) audio.srcObject = null;
      setActiveSession(null);
      set({ isMuted: false });
    }
  },

  toggleMute: () => {
    const { activeSession, isMuted } = get();
    if (!activeSession) return;
    const sdh = activeSession.sessionDescriptionHandler as Web.SessionDescriptionHandler | undefined;
    sdh?.enableSenderTracks(isMuted);
    set({ isMuted: !isMuted });
  },

  replaceInputTrack: async () => {
    const { activeSession, isMuted } = get();
    if (!activeSession) return;
    const sdh = activeSession.sessionDescriptionHandler as Web.SessionDescriptionHandler | undefined;
    const pc = sdh?.peerConnection;
    if (!pc) return;
    const inputDeviceId = useWebrtcAudioDeviceStore.getState().audioInputDeviceId;
    const audioConstraint: boolean | MediaTrackConstraints = inputDeviceId ? { deviceId: { exact: inputDeviceId } } : true;
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: audioConstraint, video: false });
    } catch (err) {
      console.warn('[WebRTC] replaceInputTrack: getUserMedia failed', err);
      return;
    }
    const newTrack = stream.getAudioTracks()[0];
    if (!newTrack) {
      stream.getTracks().forEach(t => t.stop());
      return;
    }
    newTrack.enabled = !isMuted;
    const sender = pc.getSenders().find(s => s.track?.kind === 'audio');
    if (sender) {
      await sender.replaceTrack(newTrack);
    }
    sdh?.enableSenderTracks(!isMuted);
    stream.getTracks().forEach(t => {
      if (t !== newTrack) t.stop();
    });
  },
}));
